import { ApiError } from "@/lib/api/api-error";
import { AgentGatewayService } from "@/services/agent-gateway-service";
import type { AgentSession } from "@/services/types/agent-gateway";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { mergeNormalizedEvents } from "./message-utils";
import { agentGatewayQueryKeys } from "./query-options";

type InputMutationVariables = {
  interruptId: string;
  response: unknown;
  idempotencyKey: string;
};

function getInputError(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 404 || error.status === 409) {
      return "This request was already answered or is no longer pending.";
    }
    if (error.status === 400) {
      return "Review the answer and try again.";
    }
    if (error.status === 403) {
      return "You no longer have permission to answer this request.";
    }
  }
  return "The answer could not be submitted. Please try again.";
}

export function useAgentRequestInputController(
  agentKey: string,
  sessionId: string,
) {
  const queryClient = useQueryClient();
  const scopeKey = `${agentKey}:${sessionId}`;
  const inFlightRef = useRef<string | null>(null);
  const [pendingInputId, setPendingInputId] = useState<string | null>(null);
  const [errorByInputId, setErrorByInputId] = useState<Record<string, string>>(
    {},
  );

  const mutation = useMutation({
    mutationFn: ({
      interruptId,
      response,
      idempotencyKey,
    }: InputMutationVariables) =>
      AgentGatewayService.respondToInput(
        agentKey,
        sessionId,
        interruptId,
        response,
        idempotencyKey,
      ),
    onSuccess: (response) => {
      inFlightRef.current = null;
      const sessionKey = agentGatewayQueryKeys.session(agentKey, sessionId);
      queryClient.setQueryData<AgentSession>(sessionKey, (current) =>
        current
          ? {
              ...current,
              events: mergeNormalizedEvents(current.events, response.events),
            }
          : current,
      );
      setErrorByInputId((current) => {
        const next = { ...current };
        for (const event of response.events) {
          if (event.type === "input_recorded") {
            delete next[event.content.interrupt_id];
          }
        }
        return next;
      });
      setPendingInputId(null);
      void queryClient.invalidateQueries({ exact: true, queryKey: sessionKey });
      void queryClient.invalidateQueries({
        exact: true,
        queryKey: agentGatewayQueryKeys.sessions(agentKey),
      });
    },
    onError: (error, variables) => {
      inFlightRef.current = null;
      setPendingInputId(null);
      setErrorByInputId((current) => ({
        ...current,
        [variables.interruptId]: getInputError(error),
      }));
    },
  });

  useEffect(() => {
    void scopeKey;
    inFlightRef.current = null;
    setPendingInputId(null);
    setErrorByInputId({});
  }, [scopeKey]);

  const respondToInput = (interruptId: string, response: unknown) => {
    if (inFlightRef.current !== null) {
      return;
    }
    inFlightRef.current = interruptId;
    setPendingInputId(interruptId);
    setErrorByInputId((current) => {
      const next = { ...current };
      delete next[interruptId];
      return next;
    });
    mutation.mutate({
      interruptId,
      response,
      idempotencyKey: crypto.randomUUID(),
    });
  };

  return {
    errorByInputId,
    pendingInputId,
    respondToInput,
  };
}
